import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function SessionSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    timeoutMinutes: 30,
    warningMinutes: 1,
    isEnabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user.isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/Settings/session-timeout", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (settings.timeoutMinutes < 1) {
      setError("Timeout must be at least 1 minute");
      return;
    }

    if (settings.warningMinutes >= settings.timeoutMinutes) {
      setError("Warning time must be less than timeout");
      return;
    }

    try {
      setSaving(true);
      const response = await fetch("/api/Settings/session-timeout", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to save");
      }

      setSuccess("Session settings saved successfully! Changes will apply on next login.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const presets = [
    { label: "5 minutes (Testing)", timeout: 5, warning: 1 },
    { label: "15 minutes (High Security)", timeout: 15, warning: 2 },
    { label: "30 minutes (Standard)", timeout: 30, warning: 2 },
    { label: "1 hour (Relaxed)", timeout: 60, warning: 5 },
    { label: "2 hours (Extended)", timeout: 120, warning: 5 },
  ];

  if (loading) {
    return <div style={styles.loading}>Loading settings...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>⏱️ Session Timeout Settings</h1>
        <button onClick={() => navigate("/admin")} style={styles.backButton}>
          ← Back to Admin
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Configure Session Timeout</h2>
        <p style={styles.description}>
          Set how long users can remain idle before being automatically logged out.
          A warning will appear before the session expires.
        </p>

        <form onSubmit={handleSave}>
          {/* Enable/Disable Toggle */}
          <div style={styles.toggleSection}>
            <label style={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={settings.isEnabled}
                onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
                style={styles.checkbox}
              />
              <span style={styles.toggleText}>
                {settings.isEnabled ? "✅ Session Timeout Enabled" : "❌ Session Timeout Disabled"}
              </span>
            </label>
          </div>

          {settings.isEnabled && (
            <>
              {/* Quick Presets */}
              <div style={styles.presetsSection}>
                <label style={styles.label}>Quick Presets:</label>
                <div style={styles.presetButtons}>
                  {presets.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setSettings({
                        ...settings,
                        timeoutMinutes: preset.timeout,
                        warningMinutes: preset.warning
                      })}
                      style={{
                        ...styles.presetButton,
                        ...(settings.timeoutMinutes === preset.timeout ? styles.presetActive : {})
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Settings */}
              <div style={styles.inputSection}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Session Timeout (minutes)</label>
                  <input
                    type="number"
                    value={settings.timeoutMinutes}
                    onChange={(e) => setSettings({
                      ...settings,
                      timeoutMinutes: parseInt(e.target.value) || 0
                    })}
                    min="1"
                    max="480"
                    style={styles.input}
                  />
                  <span style={styles.hint}>How long before user is logged out (1-480 minutes)</span>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Warning Time (minutes before logout)</label>
                  <input
                    type="number"
                    value={settings.warningMinutes}
                    onChange={(e) => setSettings({
                      ...settings,
                      warningMinutes: parseInt(e.target.value) || 0
                    })}
                    min="1"
                    max="10"
                    style={styles.input}
                  />
                  <span style={styles.hint}>When to show warning (1-10 minutes before timeout)</span>
                </div>
              </div>

              {/* Preview */}
              <div style={styles.preview}>
                <h3 style={styles.previewTitle}>📋 Summary</h3>
                <ul style={styles.previewList}>
                  <li>Users will be logged out after <strong>{settings.timeoutMinutes} minutes</strong> of inactivity</li>
                  <li>Warning popup will appear <strong>{settings.warningMinutes} minute(s)</strong> before logout</li>
                  <li>Any mouse/keyboard activity resets the timer</li>
                </ul>
              </div>
            </>
          )}

          <div style={styles.buttonSection}>
            <button type="submit" disabled={saving} style={styles.saveButton}>
              {saving ? "Saving..." : "💾 Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "800px",
    margin: "0 auto",
    padding: "20px",
  },
  loading: {
    textAlign: "center",
    marginTop: "100px",
    fontSize: "18px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px",
  },
  backButton: {
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  },
  error: {
    backgroundColor: "#fee",
    color: "#c33",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #fcc",
  },
  success: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #c3e6cb",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    padding: "30px",
  },
  cardTitle: {
    margin: "0 0 10px 0",
    color: "#333",
  },
  description: {
    color: "#666",
    marginBottom: "25px",
    lineHeight: "1.6",
  },
  toggleSection: {
    marginBottom: "25px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  toggleLabel: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  checkbox: {
    width: "20px",
    height: "20px",
    marginRight: "10px",
    cursor: "pointer",
  },
  toggleText: {
    fontSize: "16px",
    fontWeight: "600",
  },
  presetsSection: {
    marginBottom: "25px",
  },
  label: {
    display: "block",
    fontWeight: "600",
    marginBottom: "10px",
    color: "#333",
  },
  presetButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  presetButton: {
    padding: "10px 15px",
    backgroundColor: "#f0f0f0",
    border: "2px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
  },
  presetActive: {
    backgroundColor: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  inputSection: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "25px",
  },
  inputGroup: {
    marginBottom: "5px",
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxSizing: "border-box",
  },
  hint: {
    display: "block",
    fontSize: "12px",
    color: "#888",
    marginTop: "5px",
  },
  preview: {
    backgroundColor: "#e8f4f8",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "25px",
    border: "1px solid #bee5eb",
  },
  previewTitle: {
    margin: "0 0 10px 0",
    color: "#0c5460",
  },
  previewList: {
    margin: 0,
    paddingLeft: "20px",
    color: "#0c5460",
    lineHeight: "1.8",
  },
  buttonSection: {
    textAlign: "right",
  },
  saveButton: {
    padding: "15px 40px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

